import { httpGet } from './http'
import type {
  AdminOperationRow,
  CreditsResponse,
  FinanceOverview,
  FinanceScope,
  MonthlyRevenue,
  Page,
  ProblemsResponse,
  ProjectsResponse,
  StripeLogRow,
  UpcomingResponse,
  UserTimeline,
} from './financeTypes'

/**
 * Read-only admin finance routes (docs/admin-finance-api.md). `projectId`
 * undefined = the cross-project view (global admin only); a project admin must
 * always pass one (the API answers 422 `project_required` otherwise — there is
 * no silent fallback). No write route exists here: actions in the UI link to
 * the existing write pages.
 */
const base = '/admin/finance'

export interface PeriodQuery {
  from?: string
  to?: string
}

export const adminFinance = {
  scope: () => httpGet<FinanceScope>(`${base}/scope`),
  overview: (projectId: string | undefined, p: PeriodQuery = {}) =>
    httpGet<FinanceOverview>(`${base}/overview`, { query: { project_id: projectId, from: p.from, to: p.to } }),
  monthlyRevenue: (projectId: string | undefined, months = 12) =>
    httpGet<MonthlyRevenue>(`${base}/revenue/monthly`, { query: { project_id: projectId, months } }),
  projects: (p: PeriodQuery = {}) => httpGet<ProjectsResponse>(`${base}/projects`, { query: { from: p.from, to: p.to } }),
  problems: (projectId: string | undefined, kind?: string) =>
    httpGet<ProblemsResponse>(`${base}/problems`, { query: { project_id: projectId, kind } }),
  upcoming: (projectId: string | undefined, days = 7) =>
    httpGet<UpcomingResponse>(`${base}/upcoming`, { query: { project_id: projectId, days } }),
  credits: (projectId: string | undefined) => httpGet<CreditsResponse>(`${base}/credits`, { query: { project_id: projectId } }),
  userTimeline: (projectId: string | undefined, email: string) =>
    httpGet<UserTimeline>(`${base}/users/timeline`, { query: { project_id: projectId, email } }),
  stripeLog: (projectId: string | undefined, o: { email?: string; result?: string; limit?: number; before?: string } = {}) =>
    httpGet<Page<StripeLogRow>>(`${base}/stripe-log`, {
      query: { project_id: projectId, email: o.email, result: o.result, limit: o.limit, before: o.before },
    }),
  adminOperations: (projectId: string | undefined, o: { admin?: string; limit?: number; before?: string } = {}) =>
    httpGet<Page<AdminOperationRow>>(`${base}/admin-operations`, {
      query: { project_id: projectId, admin: o.admin, limit: o.limit, before: o.before },
    }),
}
